import mapValues from 'lodash/mapValues';

type ByteFormat =
  | 'formatBytesInteger'
  | 'formatBytesDecimalTwoDigitsMax'
  | 'formatBitsPerSecondInteger'
  | 'formatBitsPerSecondDecimalTwoDigitsMax'
  | 'formatBytesPerSecondInteger'
  | 'formatBytesPerSecondDecimalTwoDigitsMax';
type ByteFormatUnits =
  | 'formatBytes'
  | 'formatKilobytes'
  | 'formatMegabytes'
  | 'formatGigabytes'
  | 'formatTerabytes'
  | 'formatPetabytes';
export type ByteFormatters = Record<ByteFormat, (value: number) => string>;

interface ByteFormatterConfig {
  intlOptions: Record<ByteFormatUnits, Intl.NumberFormatOptions>;
  base: number;
  maxExponent: number;
}

const buildBytesFormatter = (
  lng: string | string[] | undefined,
  config: ByteFormatterConfig,
): ((value: number) => string) => {
  const { intlOptions, base, maxExponent } = config;

  const byteFormatters = mapValues(intlOptions, (options) => {
    const formatter = new Intl.NumberFormat(lng, options);
    return formatter.format.bind(formatter);
  });

  const bytesFormatter = (bytes: number) => {
    if (!bytes) return byteFormatters.formatBytes(0);
    const m = Math.min(maxExponent, Math.floor(Math.log(bytes) / Math.log(base)));
    const n = bytes / base ** m;
    switch (m) {
      case 0:
        return byteFormatters.formatBytes(n);
      case 1:
        return byteFormatters.formatKilobytes(n);
      case 2:
        return byteFormatters.formatMegabytes(n);
      case 3:
        return byteFormatters.formatGigabytes(n);
      case 4:
        return byteFormatters.formatTerabytes(n);
      // Intl only supports up to Petabytes for byte units
      // so all larger values will be represented as Petabytes
      default: {
        return byteFormatters.formatPetabytes(n);
      }
    }
  };
  return bytesFormatter;
};

const BASE_2 = 1024;
const BASE_10 = 1000;
const TERA_AS_LARGEST_PREFIX = 4;
const PETA_AS_LARGEST_PREFIX = 5;
const shortUnitDisplay: Intl.NumberFormatOptions = { style: 'unit', unitDisplay: 'short' };

const bytesIntegerFormatOptions: ByteFormatterConfig = {
  intlOptions: {
    formatBytes: { style: 'unit', unitDisplay: 'long', unit: 'byte', maximumFractionDigits: 0 },
    formatKilobytes: { ...shortUnitDisplay, unit: 'kilobyte', maximumFractionDigits: 0 },
    formatMegabytes: { ...shortUnitDisplay, unit: 'megabyte', maximumFractionDigits: 0 },
    formatGigabytes: { ...shortUnitDisplay, unit: 'gigabyte', maximumFractionDigits: 0 },
    formatTerabytes: { ...shortUnitDisplay, unit: 'terabyte', maximumFractionDigits: 0 },
    formatPetabytes: { ...shortUnitDisplay, unit: 'petabyte', maximumFractionDigits: 0 },
  },
  base: BASE_2,
  maxExponent: PETA_AS_LARGEST_PREFIX,
};

const bytesDecimalTwoDigitsMaxFormatOptions: ByteFormatterConfig = {
  ...bytesIntegerFormatOptions,
  intlOptions: {
    formatBytes: { ...bytesIntegerFormatOptions.intlOptions.formatBytes, maximumFractionDigits: 2 },
    formatKilobytes: {
      ...bytesIntegerFormatOptions.intlOptions.formatKilobytes,
      maximumFractionDigits: 2,
    },
    formatMegabytes: {
      ...bytesIntegerFormatOptions.intlOptions.formatMegabytes,
      maximumFractionDigits: 2,
    },
    formatGigabytes: {
      ...bytesIntegerFormatOptions.intlOptions.formatGigabytes,
      maximumFractionDigits: 2,
    },
    formatTerabytes: {
      ...bytesIntegerFormatOptions.intlOptions.formatTerabytes,
      maximumFractionDigits: 2,
    },
    formatPetabytes: {
      ...bytesIntegerFormatOptions.intlOptions.formatPetabytes,
      maximumFractionDigits: 2,
    },
  },
};

const bitsPerSecondIntegerFormatOptions: ByteFormatterConfig = {
  intlOptions: {
    formatBytes: { ...shortUnitDisplay, unit: 'bit-per-second', maximumFractionDigits: 0 },
    formatKilobytes: { ...shortUnitDisplay, unit: 'kilobit-per-second', maximumFractionDigits: 0 },
    formatMegabytes: { ...shortUnitDisplay, unit: 'megabit-per-second', maximumFractionDigits: 0 },
    formatGigabytes: { ...shortUnitDisplay, unit: 'gigabit-per-second', maximumFractionDigits: 0 },
    formatTerabytes: { ...shortUnitDisplay, unit: 'terabit-per-second', maximumFractionDigits: 0 },
    // Intl does not support 'Petabit' as a unit
    formatPetabytes: { ...shortUnitDisplay, unit: 'terabit-per-second', maximumFractionDigits: 0 },
  },
  base: BASE_10,
  maxExponent: TERA_AS_LARGEST_PREFIX,
};

const bitsPerSecondDecimalTwoDigitsMaxFormatOptions: ByteFormatterConfig = {
  ...bitsPerSecondIntegerFormatOptions,
  intlOptions: {
    formatBytes: {
      ...bitsPerSecondIntegerFormatOptions.intlOptions.formatBytes,
      maximumFractionDigits: 2,
    },
    formatKilobytes: {
      ...bitsPerSecondIntegerFormatOptions.intlOptions.formatKilobytes,
      maximumFractionDigits: 2,
    },
    formatMegabytes: {
      ...bitsPerSecondIntegerFormatOptions.intlOptions.formatMegabytes,
      maximumFractionDigits: 2,
    },
    formatGigabytes: {
      ...bitsPerSecondIntegerFormatOptions.intlOptions.formatGigabytes,
      maximumFractionDigits: 2,
    },
    formatTerabytes: {
      ...bitsPerSecondIntegerFormatOptions.intlOptions.formatTerabytes,
      maximumFractionDigits: 2,
    },
    formatPetabytes: {
      ...bitsPerSecondIntegerFormatOptions.intlOptions.formatPetabytes,
      maximumFractionDigits: 2,
    },
  },
};

const bytesPerSecondIntegerFormatOptions: ByteFormatterConfig = {
  intlOptions: {
    formatBytes: { ...shortUnitDisplay, unit: 'byte-per-second', maximumFractionDigits: 0 },
    formatKilobytes: { ...shortUnitDisplay, unit: 'kilobyte-per-second', maximumFractionDigits: 0 },
    formatMegabytes: { ...shortUnitDisplay, unit: 'megabyte-per-second', maximumFractionDigits: 0 },
    formatGigabytes: { ...shortUnitDisplay, unit: 'gigabyte-per-second', maximumFractionDigits: 0 },
    formatTerabytes: { ...shortUnitDisplay, unit: 'terabyte-per-second', maximumFractionDigits: 0 },
    formatPetabytes: { ...shortUnitDisplay, unit: 'petabyte-per-second', maximumFractionDigits: 0 },
  },
  base: BASE_10,
  maxExponent: PETA_AS_LARGEST_PREFIX,
};

const bytesPerSecondDecimalTwoDigitsMaxFormatOptions: ByteFormatterConfig = {
  ...bytesPerSecondIntegerFormatOptions,
  intlOptions: {
    formatBytes: {
      ...bytesPerSecondIntegerFormatOptions.intlOptions.formatBytes,
      maximumFractionDigits: 2,
    },
    formatKilobytes: {
      ...bytesPerSecondIntegerFormatOptions.intlOptions.formatKilobytes,
      maximumFractionDigits: 2,
    },
    formatMegabytes: {
      ...bytesPerSecondIntegerFormatOptions.intlOptions.formatMegabytes,
      maximumFractionDigits: 2,
    },
    formatGigabytes: {
      ...bytesPerSecondIntegerFormatOptions.intlOptions.formatGigabytes,
      maximumFractionDigits: 2,
    },
    formatTerabytes: {
      ...bytesPerSecondIntegerFormatOptions.intlOptions.formatTerabytes,
      maximumFractionDigits: 2,
    },
    formatPetabytes: {
      ...bytesPerSecondIntegerFormatOptions.intlOptions.formatPetabytes,
      maximumFractionDigits: 2,
    },
  },
};

export const getBytesFormatters = (lng: string | string[] | undefined): ByteFormatters => ({
  formatBytesInteger: buildBytesFormatter(lng, bytesIntegerFormatOptions),
  formatBytesDecimalTwoDigitsMax: buildBytesFormatter(lng, bytesDecimalTwoDigitsMaxFormatOptions),
  formatBitsPerSecondInteger: buildBytesFormatter(lng, bitsPerSecondIntegerFormatOptions),
  formatBitsPerSecondDecimalTwoDigitsMax: buildBytesFormatter(
    lng,
    bitsPerSecondDecimalTwoDigitsMaxFormatOptions,
  ),
  formatBytesPerSecondInteger: buildBytesFormatter(lng, bytesPerSecondIntegerFormatOptions),
  formatBytesPerSecondDecimalTwoDigitsMax: buildBytesFormatter(
    lng,
    bytesPerSecondDecimalTwoDigitsMaxFormatOptions,
  ),
});
